import { useState, useEffect } from 'react';
import { X, RotateCcw } from 'lucide-react';
import { useDashboardConfig, useSaveDashboardConfig, type WidgetConfig } from '../hooks/useDashboard.js';

const WIDGET_LABELS: Record<string, string> = {
  kpi_tiles: 'KPI Tegels',
  dossier_lijst: 'Dossierlijst',
  alerts: 'Alerts & Aandachtspunten',
  verkoper_performance: 'Verkoper Performance',
  omzet_grafiek: 'Omzet Grafieken',
  geografie_mini: 'Geografie Mini-kaart',
  recente_activiteit: 'Recente Activiteit',
};

const DEFAULT_WIDGETS: WidgetConfig[] = Object.keys(WIDGET_LABELS).map((id, i) => ({
  widgetId: id, positie: i, zichtbaar: true,
}));

interface Props {
  open: boolean;
  onClose: () => void;
}

export function WidgetConfigModal({ open, onClose }: Props) {
  const { data: config } = useDashboardConfig();
  const saveConfig = useSaveDashboardConfig();
  const [widgets, setWidgets] = useState<WidgetConfig[]>(DEFAULT_WIDGETS);

  useEffect(() => {
    if (config?.widgetLayout) {
      // Merge with all known widgets (in case new ones were added)
      const existing = config.widgetLayout as WidgetConfig[];
      const merged = DEFAULT_WIDGETS.map(dw => {
        const found = existing.find(e => e.widgetId === dw.widgetId);
        return found || dw;
      });
      setWidgets(merged);
    }
  }, [config]);

  if (!open) return null;

  const toggle = (widgetId: string) => {
    setWidgets(prev => prev.map(w =>
      w.widgetId === widgetId ? { ...w, zichtbaar: !w.zichtbaar } : w
    ));
  };

  const handleSave = async () => {
    await saveConfig.mutateAsync({ widgetLayout: widgets });
    onClose();
  };

  const handleReset = async () => {
    setWidgets(DEFAULT_WIDGETS);
    await saveConfig.mutateAsync({ widgetLayout: DEFAULT_WIDGETS });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl p-6 shadow-xl w-full max-w-sm space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">Personaliseer Dashboard</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-2">
          {widgets.map(w => (
            <label key={w.widgetId} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={w.zichtbaar}
                onChange={() => toggle(w.widgetId)}
                className="w-4 h-4 rounded border-gray-300 text-gold focus:ring-gold"
              />
              <span className="text-sm text-gray-700">{WIDGET_LABELS[w.widgetId] || w.widgetId}</span>
            </label>
          ))}
        </div>

        <div className="flex gap-2 justify-between pt-2 border-t border-gray-100">
          <button
            onClick={handleReset}
            className="px-3 py-1.5 rounded-lg text-sm text-gray-500 hover:bg-gray-100 flex items-center gap-1"
          >
            <RotateCcw size={14} /> Reset
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm text-gray-600 bg-gray-100 hover:bg-gray-200">
              Annuleren
            </button>
            <button
              onClick={handleSave}
              disabled={saveConfig.isPending}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gold text-white hover:bg-[#A88B3D]"
            >
              Opslaan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
